import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MOCK_PRODUCTS } from '@/data/mockData';

const CartContext = createContext();

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('chronos_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('chronos_session_id', sessionId);
  }
  return sessionId;
};

// Get cart from localStorage
const getLocalCart = () => {
  try {
    const cart = localStorage.getItem('chronos_cart');
    return cart ? JSON.parse(cart) : [];
  } catch {
    return [];
  }
};

// Save cart to localStorage
const saveLocalCart = (cart) => {
  localStorage.setItem('chronos_cart', JSON.stringify(cart));
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const sessionId = getSessionId();

  const fetchCart = useCallback(async () => {
    // If no backend, use localStorage
    if (!API) {
      setCart(getLocalCart());
      return;
    }

    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCart(getLocalCart());
    }
  }, [sessionId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, size, quantity = 1) => {
    setIsLoading(true);

    // If no backend, use localStorage
    if (!API) {
      const product = MOCK_PRODUCTS.find(p => p.id === productId);
      if (!product) {
        setIsLoading(false);
        return;
      }

      const currentCart = getLocalCart();
      const existingIndex = currentCart.findIndex(
        item => item.product_id === productId && item.size === size
      );

      if (existingIndex >= 0) {
        currentCart[existingIndex].quantity += quantity;
      } else {
        currentCart.push({
          id: 'item_' + Math.random().toString(36).substr(2, 9),
          product_id: productId,
          size,
          quantity,
          product
        });
      }

      saveLocalCart(currentCart);
      setCart(currentCart);
      setIsCartOpen(true);
      setIsLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/cart`, {
        product_id: productId,
        size: size,
        quantity: quantity,
        session_id: sessionId
      });
      await fetchCart();
      setIsCartOpen(true);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    setIsLoading(true);

    if (!API) {
      const currentCart = getLocalCart();
      if (quantity <= 0) {
        const newCart = currentCart.filter(item => item.id !== itemId);
        saveLocalCart(newCart);
        setCart(newCart);
      } else {
        const itemIndex = currentCart.findIndex(item => item.id === itemId);
        if (itemIndex >= 0) {
          currentCart[itemIndex].quantity = quantity;
          saveLocalCart(currentCart);
          setCart(currentCart);
        }
      }
      setIsLoading(false);
      return;
    }

    try {
      if (quantity <= 0) {
        await removeFromCart(itemId);
      } else {
        await axios.put(`${API}/cart/${itemId}?quantity=${quantity}`);
        await fetchCart();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    setIsLoading(true);

    if (!API) {
      const currentCart = getLocalCart();
      const newCart = currentCart.filter(item => item.id !== itemId);
      saveLocalCart(newCart);
      setCart(newCart);
      setIsLoading(false);
      return;
    }

    try {
      await axios.delete(`${API}/cart/${sessionId}/${itemId}`);
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = async () => {
    setIsLoading(true);

    if (!API) {
      saveLocalCart([]);
      setCart([]);
      setIsLoading(false);
      return;
    }

    try {
      await axios.delete(`${API}/cart/${sessionId}`);
      setCart([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      return total + (item.product?.price || 0) * item.quantity;
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      isLoading,
      isCartOpen,
      setIsCartOpen,
      sessionId,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      getCartTotal,
      getCartCount,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

